import { MoodboardItem, AiInsights } from '../types/moodboard'

export function computeAiInsights(items: MoodboardItem[]): AiInsights {
  if (items.length === 0) {
    return {
      dominantColors: [],
      themes: [],
      styleKeywords: [],
      summary: 'Aggiungi più elementi alla board per generare gli insights.',
      materials: [],
      cohesionScore: 0,
      directionMap: [],
    }
  }

  // Extract all tags
  const allTags = items.flatMap((item) => item.tags.map((t) => t.toLowerCase()))

  // Count tag frequencies
  const tagCounts = allTags.reduce(
    (acc, tag) => {
      acc[tag] = (acc[tag] || 0) + 1
      return acc
    },
    {} as Record<string, number>,
  )

  // Sort tags by frequency
  const sortedTags = Object.entries(tagCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([tag]) => tag)

  // Extract colors from color_palette items
  const paletteItems = items.filter((item) => item.type === 'color_palette')
  let extractedColors: string[] = []
  if (paletteItems.length > 0) {
    extractedColors = paletteItems.flatMap((item) =>
      item.content
        .split(',')
        .map((c) => c.trim())
        .filter((c) => c.startsWith('#')),
    )
  } else {
    // Fallback colors if no palettes exist
    extractedColors = ['#F5F5DC', '#2F4F4F', '#8B4513', '#D3D3D3']
  }

  // Deduplicate and limit colors
  const dominantColors = Array.from(new Set(extractedColors)).slice(0, 8)

  // Categorize tags (simulated AI categorization)
  const themeKeywords = [
    'minimalism',
    'minimalismo',
    'ethereal',
    'fluid',
    'earthy',
    'deconstructed',
    'avant-garde',
    'vintage',
    'futuristic',
    'romantic',
    'audacia',
    'direzione',
  ]
  const materialKeywords = [
    'silk',
    'seta',
    'leather',
    'pelle',
    'linen',
    'lino',
    'denim',
    'wool',
    'lana',
    'sheer',
    'translucent',
    'raw',
    'knit',
    'maglia',
    'tessuto',
    'cotone',
    'tweed',
  ]

  const themes = sortedTags
    .filter((tag) =>
      themeKeywords.some((tk) => tag.includes(tk) || tk.includes(tag)),
    )
    .slice(0, 4)
  if (themes.length === 0) themes.push('Contemporaneo', 'Eclettico')

  const materials = sortedTags
    .filter((tag) =>
      materialKeywords.some((mk) => tag.includes(mk) || mk.includes(tag)),
    )
    .slice(0, 4)
  if (materials.length === 0) materials.push('Mix di materiali')

  const styleKeywords = sortedTags
    .filter((tag) => !themes.includes(tag) && !materials.includes(tag))
    .slice(0, 8)

  // Generate summary based on top tags
  const topThemes = themes.slice(0, 2).join(' e ')
  const topMaterials = materials.slice(0, 2).join(', ')

  let summary = `La direzione creativa si orienta verso un\'estetica ${topThemes || 'contemporanea'}. `
  if (topMaterials) {
    summary += `Emerge un forte interesse per materiali come ${topMaterials}. `
  }
  summary += `La palette suggerisce un mood coerente, che ancora l\'identità visiva della collezione. Per aggiungere profondità, valuta di introdurre elementi di contrasto.`

  // Calculate cohesion score based on tag repetition
  const uniqueTags = new Set(allTags).size
  const totalTags = allTags.length
  // Higher ratio of total to unique means more repetition -> higher cohesion
  const ratio = totalTags > 0 ? (totalTags - uniqueTags) / totalTags : 0
  const cohesionScore = Math.min(
    Math.max(Math.round((ratio + 0.4) * 100), 30),
    95,
  )

  // Compute direction map
  // X: Tradizione (0) -> Innovazione (100)
  // Y: Minimale (0) -> Espressivo (100)
  const traditionTags = [
    'sartoria',
    'tweed',
    'tradizione',
    'classico',
    'heritage',
    'vintage',
    'lana',
    'cotone',
  ]
  const innovationTags = [
    'audacia',
    'innovazione',
    'deconstructed',
    'avant-garde',
    'futuristic',
    'tecnico',
    'sperimentale',
  ]
  const minimalTags = [
    'minimalismo',
    'opaco',
    'monocromo',
    'pulito',
    'essenziale',
    'neutro',
    'base',
  ]
  const expressiveTags = [
    'fantasia',
    'texture',
    'volume',
    'layering',
    'colore',
    'eclettico',
    'decorativo',
    'pois',
  ]

  const directionMap = items.map((item) => {
    let x = 50 // Default middle
    let y = 50 // Default middle

    const itemTags = item.tags.map((t) => t.toLowerCase())
    const text = [item.title || '', item.description || '', ...itemTags]
      .join(' ')
      .toLowerCase()

    // Adjust X
    let traditionScore = traditionTags.filter((t) => text.includes(t)).length
    let innovationScore = innovationTags.filter((t) => text.includes(t)).length
    if (traditionScore > 0 || innovationScore > 0) {
      // Shift towards whichever is stronger, map to 10-90 range
      const total = traditionScore + innovationScore
      const ratio = innovationScore / total
      x = 20 + ratio * 60 // 20 to 80
    } else {
      // Random jitter around center if no strong signals
      x = 40 + Math.random() * 20
    }

    // Adjust Y
    let minimalScore = minimalTags.filter((t) => text.includes(t)).length
    let expressiveScore = expressiveTags.filter((t) => text.includes(t)).length
    if (minimalScore > 0 || expressiveScore > 0) {
      const total = minimalScore + expressiveScore
      const ratio = expressiveScore / total
      y = 20 + ratio * 60 // 20 to 80
    } else {
      y = 40 + Math.random() * 20
    }

    return {
      id: item.id,
      x: Math.round(x),
      y: Math.round(y),
      label: item.title || item.tags[0] || 'Elemento',
    }
  })

  return {
    dominantColors,
    themes,
    styleKeywords,
    summary,
    materials,
    cohesionScore,
    directionMap,
  }
}
